import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import {
  Upload,
  ArrowRight,
  ArrowLeft,
  Check,
  X,
  Plus,
  Minus,
  Loader2,
  AlertTriangle,
  Pencil,
  Wallet,
  Landmark,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/api";
import { CropModal } from "@/CropModal";

const LOGO_URL = "/assets/magnetixs-wordmark-white.png";

const STANZ_RATIO = 96.018 / 70.5;
const itemRatio = (p) => (p?.crop?.orientation === "portrait" ? 1 / STANZ_RATIO : STANZ_RATIO);
const PRICE_MAP = { 3: 11.95, 6: 19.95, 9: 27.95, 12: 34.9 };
const BUNDLE_SIZES = [3, 6, 9, 12];
const SHIPPING = 4.9;
const VALID_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"];
const VALID_EXT = [".jpg", ".jpeg", ".png", ".webp", ".heic", ".heif"];
const MAX_BYTES = 15 * 1024 * 1024;

const STEPS = ["Produkt", "Fotos", "Warenkorb", "Adresse", "Zahlung", "Bestellen"];

const PAYMENTS = [
  { id: "paypal_card", label: "PayPal & Kartenzahlung", icon: Wallet, note: "PayPal, Kreditkarte, Lastschrift" },
  { id: "ueberweisung", label: "Vorkasse / Überweisung", icon: Landmark, note: "Bankdaten folgen per E-Mail" },
];

// Renders the official PayPal Smart Payment Buttons directly under the selected payment option.
// Creates a PayPal order (amount computed server-side from the bundle), captures it on approval,
// then hands the confirmed paypal_order_id back so the Magnetixs order can be created.
function PayPalCheckoutSection({ bundle, address, disabled, onPaid }) {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api
      .get("/paypal/config")
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig({ configured: false }));
  }, []);

  if (!config) {
    return <div className="mt-4 text-sm text-gray-500 text-center py-4">PayPal wird geladen…</div>;
  }
  if (!config.configured) {
    return (
      <div
        className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10 text-sm text-gray-400 text-center"
        data-testid="paypal-not-configured"
      >
        PayPal-Zahlung ist derzeit noch nicht verfügbar. Bitte wähle „Vorkasse / Überweisung“.
      </div>
    );
  }
  return (
    <div className="mt-4" data-testid="paypal-buttons-container">
      <PayPalScriptProvider options={{ clientId: config.client_id, currency: "EUR", locale: "de_DE" }}>
        <PayPalButtons
          disabled={disabled}
          style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal" }}
          createOrder={async () => {
            const { data } = await api.post("/paypal/create-order", { bundle });
            return data.id;
          }}
          onApprove={async (data) => {
            try {
              await api.post(`/paypal/capture-order/${data.orderID}`);
            } catch {
              toast.error("PayPal-Zahlung konnte nicht abgeschlossen werden.");
              return;
            }
            // Payment is already captured at this point. If saving the order fails, retry once
            // (transient hiccup) before falling back so a paid customer never loses their payment silently.
            let ok = await onPaid(data.orderID);
            if (!ok) {
              await new Promise((r) => setTimeout(r, 1500));
              ok = await onPaid(data.orderID);
            }
            if (!ok) {
              try {
                await api.post("/paypal/report-failed-order", {
                  paypal_order_id: data.orderID,
                  name: address?.name,
                  email: address?.email,
                  bundle,
                });
              } catch {
                // ignore - the toast below still gives the customer their reference number
              }
              toast.error(
                `Deine Zahlung wurde bereits verarbeitet (PayPal-Referenz: ${data.orderID}), die Bestellung konnte jedoch nicht gespeichert werden. Bitte kontaktiere uns unter Angabe dieser Referenz - wir kümmern uns sofort darum.`,
                { duration: 25000 }
              );
            }
          }}
          onError={() => toast.error("PayPal-Fehler. Bitte versuche es erneut oder wähle Vorkasse.")}
        />
      </PayPalScriptProvider>
    </div>
  );
}

const euro = (n) => `${n.toFixed(2).replace(".", ",")} €`;
const isValidFile = (f) =>
  VALID_TYPES.includes(f.type) || VALID_EXT.some((e) => f.name.toLowerCase().endsWith(e));

const WIZARD_KEY = "mtx_wizard_v1";
export const readWizard = () => {
  try {
    return JSON.parse(sessionStorage.getItem(WIZARD_KEY) || "null");
  } catch {
    return null;
  }
};
export const clearWizard = () => {
  try {
    sessionStorage.removeItem(WIZARD_KEY);
  } catch {
    /* ignore */
  }
};

const Header = () => (
  <header className="bg-[#0A0A0A] border-b border-white/10 py-5">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <img src={LOGO_URL} alt="Magnetixs" className="h-9 md:h-11 object-contain" />
    </div>
  </header>
);

const Stepper = ({ current }) => (
  <div className="flex items-center justify-center gap-1 sm:gap-2 mb-8 flex-wrap" data-testid="wizard-stepper">
    {STEPS.map((label, i) => (
      <div key={label} className="flex items-center gap-1 sm:gap-2">
        <div
          className={`flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
            i === current
              ? "bg-[#E4231F] text-white"
              : i < current
              ? "bg-white/10 text-gray-300"
              : "bg-white/5 text-gray-500"
          }`}
        >
          <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">
            {i < current ? <Check className="w-3 h-3" /> : i + 1}
          </span>
          <span className="hidden sm:inline">{label}</span>
        </div>
        {i < STEPS.length - 1 && <span className="text-white/20">·</span>}
      </div>
    ))}
  </div>
);

const Shell = ({ children, onBack, backLabel = "Zurück", maxWidth = "max-w-2xl", step }) => (
  <div className="min-h-screen bg-[#0A0A0A]">
    <Header />
    <div className="py-10">
      <div className={`${maxWidth} mx-auto px-4`}>
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
            data-testid="wizard-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            {backLabel}
          </button>
        )}
        {typeof step === "number" && <Stepper current={step} />}
        {children}
      </div>
    </div>
  </div>
);

export default function OrderWizard({ initialBundle = null, onExit }) {
  const persisted = readWizard();
  const [step, setStep] = useState(persisted ? persisted.step : initialBundle ? 1 : 0);
  const [bundle, setBundle] = useState(persisted?.bundle ?? initialBundle);
  const [positions, setPositions] = useState(persisted?.positions ?? []); // {upload_id, filename, low_res, crop, thumbnail, preview?, quantity}
  const [cropTarget, setCropTarget] = useState(null);
  const [uploading, setUploading] = useState(false);
  const queueRef = useRef([]);
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const [address, setAddress] = useState(
    persisted?.address ?? { name: "", street: "", postal_code: "", city: "", email: "" }
  );
  const [payment, setPayment] = useState(persisted?.payment ?? "ueberweisung");
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);

  // Persist wizard progress (crop, position, quantities, address) so a browser reload resumes.
  // Full previews are stripped (kept in object storage, re-fetched on edit) to stay within quota.
  useEffect(() => {
    if (step >= 6) {
      clearWizard();
      return;
    }
    try {
      const slim = positions.map(({ preview, ...rest }) => rest);
      sessionStorage.setItem(WIZARD_KEY, JSON.stringify({ bundle, step, positions: slim, address, payment }));
    } catch {
      /* quota / private mode - non fatal */
    }
  }, [bundle, step, positions, address, payment]);

  const totalQty = positions.reduce((s, p) => s + p.quantity, 0);
  const price = PRICE_MAP[bundle] || 0;
  const total = price + SHIPPING;
  const remaining = (bundle || 0) - totalQty;

  // ---------------- upload + crop queue ----------------
  const uploadNext = useCallback(async () => {
    if (queueRef.current.length === 0) return;
    const file = queueRef.current[0];
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.low_res) toast.warning(data.warning || "Dieses Bild könnte im Druck unscharf werden.");
      setCropTarget({
        upload_id: data.upload_id,
        preview: data.preview,
        filename: data.filename,
        low_res: data.low_res,
        editIndex: null,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload fehlgeschlagen.");
      queueRef.current = queueRef.current.slice(1);
      if (queueRef.current.length > 0) uploadNext();
    } finally {
      setUploading(false);
    }
  }, []);

  const handleFiles = (fileList) => {
    const all = Array.from(fileList);
    const oversize = all.filter((f) => f.size > MAX_BYTES);
    if (oversize.length) toast.error("Datei zu groß – maximal 15 MB pro Foto.");
    const valid = all.filter((f) => isValidFile(f) && f.size <= MAX_BYTES);
    if (valid.length === 0) {
      if (all.length && !oversize.length) toast.error("Bitte JPG, PNG, WEBP oder HEIC hochladen.");
      return;
    }
    if (remaining <= 0) {
      toast.error(`Dein Paket umfasst ${bundle} Magnete – erhöhe die Anzahl bestehender Fotos.`);
      return;
    }
    const allowed = valid.slice(0, remaining);
    if (allowed.length < valid.length) {
      toast.warning(`Nur ${allowed.length} Foto(s) hinzugefügt (Paketgröße ${bundle}).`);
    }
    queueRef.current = allowed;
    uploadNext();
  };

  const onCropConfirm = ({ crop, thumbnail }) => {
    const t = cropTarget;
    if (t.editIndex != null) {
      setPositions((prev) =>
        prev.map((p, i) => (i === t.editIndex ? { ...p, crop, thumbnail, preview: t.preview } : p))
      );
      setCropTarget(null);
      return;
    }
    setPositions((prev) => [
      ...prev,
      {
        upload_id: t.upload_id,
        filename: t.filename,
        low_res: t.low_res,
        preview: t.preview,
        crop,
        thumbnail,
        quantity: 1,
      },
    ]);
    setCropTarget(null);
    queueRef.current = queueRef.current.slice(1);
    if (queueRef.current.length > 0) setTimeout(uploadNext, 80);
  };

  const onCropCancel = () => {
    const wasEdit = cropTarget?.editIndex != null;
    setCropTarget(null);
    if (!wasEdit) {
      queueRef.current = queueRef.current.slice(1);
      if (queueRef.current.length > 0) setTimeout(uploadNext, 80);
    }
  };

  const editPosition = async (i) => {
    let preview = positions[i].preview;
    if (!preview) {
      // preview was stripped after a page reload -> re-fetch from storage by upload_id
      try {
        const { data } = await api.get(`/upload/${positions[i].upload_id}`);
        preview = data.preview;
      } catch {
        toast.error("Foto konnte nicht geladen werden – es ist möglicherweise abgelaufen.");
        return;
      }
    }
    setCropTarget({
      upload_id: positions[i].upload_id,
      preview,
      filename: positions[i].filename,
      low_res: positions[i].low_res,
      editIndex: i,
      initialArea: positions[i].crop,
    });
  };

  const changeQty = (i, delta) =>
    setPositions((prev) =>
      prev.map((p, idx) => {
        if (idx !== i) return p;
        const q = p.quantity + delta;
        if (q < 1) return p;
        if (delta > 0 && totalQty >= bundle) return p;
        return { ...p, quantity: q };
      })
    );

  const removePosition = (i) => setPositions((prev) => prev.filter((_, idx) => idx !== i));

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  // ---------------- submit ----------------
  const submitOrder = async (paypalOrderId = null) => {
    setSubmitting(true);
    try {
      // One entry per distinct motif with its quantity (backend creates one production file per motif)
      const items = positions.map((p) => ({ upload_id: p.upload_id, crop: p.crop, quantity: p.quantity }));
      const { data } = await api.post("/orders", {
        name: address.name,
        street: address.street,
        city: address.city,
        postal_code: address.postal_code,
        email: address.email,
        bundle,
        items,
        payment,
        paypal_order_id: paypalOrderId,
      });
      setOrderResult(data);
      setStep(6);
      toast.success("Bestellung erfolgreich aufgegeben!");
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler bei der Bestellung.");
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const validateAddress = () => {
    if (!address.name || !address.street || !address.postal_code || !address.city) {
      toast.error("Bitte fülle alle Adressfelder aus.");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address.email)) {
      toast.error("Bitte gib eine gültige E-Mail-Adresse ein.");
      return false;
    }
    return true;
  };

  // ================= RENDER =================

  // Step 0: Produkt & Menge
  if (step === 0) {
    return (
      <Shell onBack={onExit} backLabel="Zur Startseite" step={0}>
        <div className="bg-[#141416] border border-white/10 rounded-2xl shadow-lg p-8">
          <h1 className="text-2xl font-bold text-white">Produkt & Menge wählen</h1>
          <p className="text-gray-400 mt-2 mb-6">Wie viele Fotomagnete möchtest du gestalten?</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {BUNDLE_SIZES.map((b) => (
              <button
                key={b}
                type="button"
                onClick={() => setBundle(b)}
                data-testid={`product-${b}`}
                className={`p-6 border-2 rounded-2xl text-left transition-all flex flex-col ${
                  bundle === b ? "border-[#E4231F] bg-[#E4231F]/10" : "border-white/10 hover:border-white/30"
                }`}
              >
                <div className="text-xs uppercase tracking-wider text-gray-500 min-h-[2.5em]">
                  {b === 12 ? "Spar-Paket · beliebt" : b === 3 ? "Mini" : "Paket"}
                </div>
                <div className="font-bold text-3xl text-white mt-1">{b}</div>
                <div className="text-sm text-gray-400">Magnete</div>
                <div className="text-[#E4231F] font-bold text-xl mt-2">{euro(PRICE_MAP[b])}</div>
                <div className="text-xs text-gray-500 mt-1 mt-auto pt-1">
                  {euro(PRICE_MAP[b] / b)}/Magnet · +{euro(SHIPPING)} Versand
                </div>
              </button>
            ))}
          </div>
          <Button
            onClick={() => bundle && setStep(1)}
            disabled={!bundle}
            data-testid="product-continue-btn"
            className="w-full mt-8 h-14 rounded-full bg-[#E4231F] hover:bg-red-700 text-white text-lg font-semibold disabled:opacity-50"
          >
            Weiter zu den Fotos
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </Shell>
    );
  }

  // Step 1: Fotos hochladen & zuschneiden
  if (step === 1) {
    return (
      <Shell onBack={() => setStep(0)} step={1} maxWidth="max-w-3xl">
        <div className="bg-[#141416] border border-white/10 rounded-2xl shadow-lg p-8">
          <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-white">Fotos hochladen & zuschneiden</h1>
              <p className="text-gray-400 mt-1">Jedes Foto wird auf das Magnetformat 78 × 53 mm zugeschnitten.</p>
            </div>
            <div
              className={`text-sm font-bold px-4 py-2 rounded-full flex-shrink-0 whitespace-nowrap ${
                totalQty === bundle ? "bg-green-500/15 text-green-400" : "bg-white/10 text-gray-300"
              }`}
              data-testid="magnet-counter"
            >
              {totalQty} / {bundle}
            </div>
          </div>

          {remaining > 0 && (
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => !uploading && fileInputRef.current?.click()}
              data-testid="upload-zone"
              className={`border-2 border-dashed rounded-2xl p-10 cursor-pointer transition-all flex flex-col items-center justify-center gap-3 ${
                dragActive ? "border-[#E4231F] bg-[#E4231F]/10" : "border-white/15 bg-white/5 hover:border-white/30"
              } ${uploading ? "pointer-events-none opacity-60" : ""}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
                multiple
                onChange={(e) => {
                  if (e.target.files?.length) handleFiles(e.target.files);
                  e.target.value = ""; // reset so selecting the same file again still fires onChange
                }}
                className="hidden"
                data-testid="file-input"
              />
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
                {uploading ? (
                  <Loader2 className="w-8 h-8 text-gray-300 animate-spin" />
                ) : (
                  <Upload className="w-8 h-8 text-gray-300" />
                )}
              </div>
              <p className="font-bold text-lg text-white">
                {uploading ? "Wird verarbeitet…" : positions.length ? "Weiteres Foto hinzufügen" : "Fotos hier ablegen"}
              </p>
              <p className="text-gray-400 text-sm">oder klicken · JPG, PNG, WEBP, HEIC · max. 15 MB</p>
            </div>
          )}

          {positions.length > 0 && (
            <div className="mt-8 space-y-4">
              <h3 className="font-bold text-white">Deine Motive</h3>
              {positions.map((p, i) => (
                <div
                  key={i}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 border border-white/10 rounded-xl p-3"
                  data-testid={`position-${i}`}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div
                      className="w-24 flex-shrink-0 rounded-lg overflow-hidden border border-white/10 bg-white/5"
                      style={{ aspectRatio: `${itemRatio(p)}` }}
                    >
                      <img src={p.thumbnail} alt={p.filename} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{p.filename}</p>
                      {p.low_res && (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-400 mt-1">
                          <AlertTriangle className="w-3 h-3" /> Niedrige Auflösung
                        </span>
                      )}
                      <button
                        onClick={() => editPosition(i)}
                        data-testid={`edit-position-${i}`}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#E4231F] mt-1"
                      >
                        <Pencil className="w-3 h-3" /> Zuschnitt bearbeiten
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-2 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => changeQty(i, -1)}
                        disabled={p.quantity <= 1}
                        data-testid={`qty-minus-${i}`}
                        className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center disabled:opacity-40 text-gray-300 hover:border-white/40"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-6 text-center font-bold text-white" data-testid={`qty-value-${i}`}>
                        {p.quantity}
                      </span>
                      <button
                        onClick={() => changeQty(i, 1)}
                        disabled={totalQty >= bundle}
                        data-testid={`qty-plus-${i}`}
                        className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center disabled:opacity-40 text-gray-300 hover:border-white/40"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      onClick={() => removePosition(i)}
                      data-testid={`remove-position-${i}`}
                      className="w-8 h-8 rounded-full text-gray-500 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalQty > 0 && totalQty !== bundle && (
            <p className="text-sm text-amber-400 mt-4 text-center">
              Noch {Math.abs(remaining)} Magnet(e) {remaining > 0 ? "auswählen" : "zu viel"} – dein Paket umfasst genau {bundle}.
            </p>
          )}

          <Button
            onClick={() => setStep(2)}
            disabled={totalQty !== bundle}
            data-testid="photos-continue-btn"
            className="w-full mt-8 h-14 rounded-full bg-[#E4231F] hover:bg-red-700 text-white text-lg font-semibold disabled:opacity-50"
          >
            Weiter zum Warenkorb
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>

        {cropTarget && (
          <CropModal
            image={cropTarget.preview}
            filename={cropTarget.filename}
            initialArea={cropTarget.initialArea || null}
            onCancel={onCropCancel}
            onConfirm={onCropConfirm}
          />
        )}
      </Shell>
    );
  }

  // Step 2: Warenkorb
  if (step === 2) {
    return (
      <Shell onBack={() => setStep(1)} step={2} maxWidth="max-w-3xl">
        <div className="bg-[#141416] border border-white/10 rounded-2xl shadow-lg p-8">
          <h1 className="text-2xl font-bold text-white mb-6">Dein Warenkorb</h1>
          <div className="space-y-3">
            {positions.map((p, i) => (
              <div key={i} className="flex items-center gap-4 border-b border-white/10 pb-3" data-testid={`cart-item-${i}`}>
                <div className="w-16 rounded-lg overflow-hidden border border-white/10" style={{ aspectRatio: `${itemRatio(p)}` }}>
                  <img src={p.thumbnail} alt={p.filename} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white truncate">{p.filename}</p>
                  <p className="text-sm text-gray-400">Fotomagnet 78 × 53 mm</p>
                </div>
                <span className="font-bold text-white">× {p.quantity}</span>
              </div>
            ))}
          </div>
          <div className="bg-white/5 rounded-xl p-5 mt-6 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">{bundle} Fotomagnete</span>
              <span className="font-semibold text-white">{euro(price)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Versand</span>
              <span className="font-semibold text-white">{euro(SHIPPING)}</span>
            </div>
            <hr className="border-white/10" />
            <div className="flex justify-between text-lg">
              <span className="font-bold text-white">Gesamt</span>
              <span className="font-bold text-[#E4231F]" data-testid="cart-total">
                {euro(total)}
              </span>
            </div>
          </div>
          <Button
            onClick={() => setStep(3)}
            data-testid="cart-continue-btn"
            className="w-full mt-8 h-14 rounded-full bg-[#E4231F] hover:bg-red-700 text-white text-lg font-semibold"
          >
            Weiter zur Lieferadresse
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </Shell>
    );
  }

  // Step 3: Lieferadresse
  if (step === 3) {
    return (
      <Shell onBack={() => setStep(2)} step={3}>
        <div className="bg-[#141416] border border-white/10 rounded-2xl shadow-lg p-8">
          <h1 className="text-2xl font-bold text-white mb-6">Lieferadresse</h1>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-300">Name *</Label>
              <Input
                data-testid="input-name"
                value={address.name}
                onChange={(e) => setAddress({ ...address, name: e.target.value })}
                placeholder="Max Mustermann"
                className="mt-1 h-12 rounded-lg bg-[#0A0A0A] border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-[#E4231F]"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-300">Straße & Hausnummer *</Label>
              <Input
                data-testid="input-street"
                value={address.street}
                onChange={(e) => setAddress({ ...address, street: e.target.value })}
                placeholder="Musterstraße 123"
                className="mt-1 h-12 rounded-lg bg-[#0A0A0A] border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-[#E4231F]"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-medium text-gray-300">PLZ *</Label>
                <Input
                  data-testid="input-postal-code"
                  value={address.postal_code}
                  onChange={(e) => setAddress({ ...address, postal_code: e.target.value })}
                  placeholder="12345"
                  className="mt-1 h-12 rounded-lg bg-[#0A0A0A] border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-[#E4231F]"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-sm font-medium text-gray-300">Stadt *</Label>
                <Input
                  data-testid="input-city"
                  value={address.city}
                  onChange={(e) => setAddress({ ...address, city: e.target.value })}
                  placeholder="Musterstadt"
                  className="mt-1 h-12 rounded-lg bg-[#0A0A0A] border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-[#E4231F]"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-300">E-Mail *</Label>
              <Input
                type="email"
                data-testid="input-email"
                value={address.email}
                onChange={(e) => setAddress({ ...address, email: e.target.value })}
                placeholder="mail@beispiel.de"
                className="mt-1 h-12 rounded-lg bg-[#0A0A0A] border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-[#E4231F]"
              />
              <p className="text-xs text-gray-500 mt-1">Für Bestellbestätigung und Zahlungsinformationen.</p>
            </div>
          </div>
          <Button
            onClick={() => validateAddress() && setStep(4)}
            data-testid="address-continue-btn"
            className="w-full mt-8 h-14 rounded-full bg-[#E4231F] hover:bg-red-700 text-white text-lg font-semibold"
          >
            Weiter zur Zahlung
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </Shell>
    );
  }

  // Step 4: Zahlungsmethode
  if (step === 4) {
    return (
      <Shell onBack={() => setStep(3)} step={4}>
        <div className="bg-[#141416] border border-white/10 rounded-2xl shadow-lg p-8">
          <h1 className="text-2xl font-bold text-white mb-2">Zahlungsmethode</h1>
          <p className="text-gray-400 mb-6">Wähle, wie du bezahlen möchtest.</p>
          <div className="space-y-3">
            {PAYMENTS.map((m) => (
              <button
                key={m.id}
                onClick={() => setPayment(m.id)}
                data-testid={`payment-${m.id}`}
                className={`w-full flex items-center gap-4 p-4 border-2 rounded-xl text-left transition-all ${
                  payment === m.id ? "border-[#E4231F] bg-[#E4231F]/10" : "border-white/10 hover:border-white/30"
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <m.icon className="w-5 h-5 text-gray-300" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-white">{m.label}</p>
                  <p className="text-sm text-gray-400">{m.note}</p>
                </div>
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    payment === m.id ? "border-[#E4231F]" : "border-white/20"
                  }`}
                >
                  {payment === m.id && <div className="w-2.5 h-2.5 rounded-full bg-[#E4231F]" />}
                </div>
              </button>
            ))}
          </div>
          {payment === "paypal_card" ? (
            <PayPalCheckoutSection bundle={bundle} address={address} disabled={submitting} onPaid={submitOrder} />
          ) : (
            <Button
              onClick={() => setStep(5)}
              data-testid="payment-continue-btn"
              className="w-full mt-8 h-14 rounded-full bg-[#E4231F] hover:bg-red-700 text-white text-lg font-semibold"
            >
              Weiter zur Übersicht
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          )}
        </div>
      </Shell>
    );
  }

  // Step 5: Abschließende Übersicht
  if (step === 5) {
    const paymentLabel = PAYMENTS.find((p) => p.id === payment)?.label;
    return (
      <Shell onBack={() => setStep(4)} step={5}>
        <div className="bg-[#141416] border border-white/10 rounded-2xl shadow-lg p-8 space-y-6">
          <h1 className="text-2xl font-bold text-white">Bestellübersicht</h1>

          <div className="grid grid-cols-4 gap-2">
            {positions.flatMap((p, pi) =>
              Array.from({ length: p.quantity }).map((_, qi) => (
                <div
                  key={`${pi}-${qi}`}
                  className="rounded-lg overflow-hidden border border-white/10"
                  style={{ aspectRatio: `${itemRatio(p)}` }}
                >
                  <img src={p.thumbnail} alt="" className="w-full h-full object-cover" />
                </div>
              ))
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="bg-white/5 rounded-xl p-4">
              <p className="font-semibold text-gray-500 uppercase text-xs mb-2">Lieferadresse</p>
              <p className="font-medium text-white">{address.name}</p>
              <p className="text-gray-300">{address.street}</p>
              <p className="text-gray-300">{address.postal_code} {address.city}</p>
              <p className="text-gray-400">{address.email}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4">
              <p className="font-semibold text-gray-500 uppercase text-xs mb-2">Zahlung</p>
              <p className="font-medium text-white">{paymentLabel}</p>
              <hr className="my-2 border-white/10" />
              <div className="flex justify-between text-gray-300"><span className="text-gray-400">{bundle} Magnete</span><span>{euro(price)}</span></div>
              <div className="flex justify-between text-gray-300"><span className="text-gray-400">Versand</span><span>{euro(SHIPPING)}</span></div>
              <div className="flex justify-between text-base mt-1"><span className="font-bold text-white">Gesamt</span><span className="font-bold text-[#E4231F]">{euro(total)}</span></div>
            </div>
          </div>

          <button
            onClick={() => submitOrder()}
            disabled={submitting}
            data-testid="submit-order-btn"
            className={`w-full flex items-center justify-center gap-3 p-4 rounded-full font-bold text-lg uppercase tracking-wide transition-all ${
              submitting ? "bg-white/10 text-gray-500 cursor-not-allowed" : "bg-[#E4231F] hover:bg-red-700 text-white shadow-lg hover:scale-[1.01]"
            }`}
          >
            {submitting ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" /> Bestellung wird verarbeitet…
              </>
            ) : (
              <>
                <Check className="w-6 h-6" /> Zahlungspflichtig bestellen
              </>
            )}
          </button>
          <p className="text-xs text-center text-gray-500">
            Mit Klick auf „Zahlungspflichtig bestellen“ akzeptierst du unsere AGB und Widerrufsbelehrung.
          </p>
          <div className="flex justify-center pt-2">
            <img
              src="/assets/gewaehrleistungslabel-de.webp"
              alt="Gesetzliche Gewährleistung: Mindestens zwei Jahre gesetzliche Gewährleistung der Vertragsmäßigkeit für Waren, die in der Europäischen Union verkauft werden."
              className="w-full max-w-xs sm:max-w-sm rounded-lg border border-white/10"
              data-testid="checkout-warranty-label"
            />
          </div>
        </div>
      </Shell>
    );
  }

  // Step 6: Erfolg
  const bank = orderResult?.bank_details;
  return (
    <Shell step={6} maxWidth="max-w-xl">
      <div className="bg-[#141416] border border-white/10 rounded-2xl shadow-lg p-8 text-center" data-testid="order-success">
        <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-5">
          <Check className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-white">Bestellung erfolgreich!</h1>
        <p className="text-gray-400 mt-2">
          Bestellnummer <span className="font-bold text-[#E4231F]">{orderResult?.order_number}</span>
        </p>
        {bank ? (
          <div
            className="bg-white/5 border border-[#E4231F]/30 rounded-xl p-5 mt-6 text-left text-sm text-gray-300 space-y-1"
            data-testid="bank-details-card"
          >
            <p className="font-semibold text-gray-500 uppercase text-xs mb-2">Bitte überweise den Betrag an</p>
            <p>Empfänger: <span className="text-white">{bank.holder || "wird per E-Mail nachgereicht"}</span></p>
            <p>IBAN: <span className="text-white">{bank.iban || "wird per E-Mail nachgereicht"}</span></p>
            <p>BIC: <span className="text-white">{bank.bic || "wird per E-Mail nachgereicht"}</span></p>
            <p>Bank: <span className="text-white">{bank.bank_name || "wird per E-Mail nachgereicht"}</span></p>
            <p>Verwendungszweck: <span className="font-bold text-[#E4231F]">{bank.reference}</span></p>
          </div>
        ) : (
          <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-5 mt-6 text-left text-sm text-gray-300">
            Deine Zahlung per PayPal/Karte wurde erfolgreich verarbeitet. Du erhältst in Kürze eine Bestellbestätigung an{" "}
            <span className="text-[#E4231F]">{address.email}</span>.
          </div>
        )}
        <p className="text-gray-400 mt-4 text-sm">
          Nach Zahlungseingang produzieren und versenden wir deine Magnete in 3–5 Werktagen.
        </p>
        <Button
          onClick={onExit}
          data-testid="back-home-btn"
          className="w-full mt-6 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white"
        >
          Zurück zur Startseite
        </Button>
      </div>
    </Shell>
  );
}
